import * as system from 'durandal/system';
import * as analytics from 'analytics';

import * as companyService from '@/legacy/services/company';
import * as userService from '@/legacy/services/user';
import * as verificationService from '@/legacy/services/verification/verification';
import * as plootoUtils from '@/legacy/utils/plooto';
import * as addModels from '@/legacy/viewmodels/addModels';
import * as viewModels from '@/legacy/viewmodels/viewModels';

const API_PREFIX = 'v1';

export class CompanyDirectorUpdateBindingModel {
  public userId: string;

  public isDirector: boolean;
}

export class VerificationCompanyService {
  public GetVerifyCompanyStatus(companyId): JQueryPromise<verificationService.VerificationStatus> {
    return system
      .defer<verificationService.VerificationStatus>((dfd) => {
        userService.Instance.AuthorizedGet(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/companies/${companyId}/verification/status`
        )
          .then((verificationInfo: verificationService.VerificationStatus) => {
            if (plootoUtils.IsNullOrUndefined(verificationInfo) || verificationInfo.error) {
              dfd.reject(verificationInfo);
              return;
            }
            dfd.resolve(verificationInfo);
          })
          .fail((verificationInfo: verificationService.VerificationStatus) => {
            dfd.reject(verificationInfo);
          });
      })
      .promise();
  }

  public GetTeamVerifications(
    companyId
  ): JQueryPromise<Array<verificationService.VerificationStatusIdentityViewModel>> {
    return system
      .defer<Array<verificationService.VerificationStatusIdentityViewModel>>((dfd) => {
        userService.Instance.AuthorizedGet(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/companies/${companyId}/verification/teamVerifications`
        )
          .then((verificationInfo: any) => {
            if (plootoUtils.IsNullOrUndefined(verificationInfo) || verificationInfo.error) {
              dfd.reject(verificationInfo);
              return;
            }
            dfd.resolve(verificationInfo);
          })
          .fail(
            (verificationInfo: Array<verificationService.VerificationStatusIdentityViewModel>) => {
              dfd.reject(verificationInfo);
            }
          );
      })
      .promise();
  }

  public GetAccountsPinsVerified(companyId): JQueryPromise<boolean> {
    return system
      .defer<boolean>((dfd) => {
        userService.Instance.AuthorizedGet(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/companies/${companyId}/verification/accounts/pinsVerified`
        )
          .then((isAllPinsVerified: any) => {
            dfd.resolve(isAllPinsVerified);
          })
          .fail((error) => {
            dfd.reject(error);
          });
      })
      .promise();
  }

  public VerifyCompany(
    companyId: string,
    formData: FormData
  ): JQueryPromise<verificationService.VerificationStatus> {
    return system
      .defer<verificationService.VerificationStatus>((dfd) => {
        userService.Instance.AuthorizedUpload(
          `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/verification`,
          formData
        )
          .done((verificationInfo: verificationService.VerificationStatus) => {
            if (plootoUtils.IsNullOrUndefined(verificationInfo) || verificationInfo.error) {
              dfd.reject(verificationInfo);
              return;
            }
            dfd.resolve(verificationInfo);
          })
          .fail((verificationInfo: verificationService.VerificationStatus) => {
            dfd.reject(verificationInfo);
          });
      })
      .promise();
  }

  public PostBusinessInformation(
    companyId: string,
    formData: addModels.BusinessInformationAddModel
  ): JQueryPromise<verificationService.VerificationStatus> {
    return system
      .defer<verificationService.VerificationStatus>((dfd) => {
        userService.Instance.AuthorizedPost(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/companies/${companyId}/verification/businessInformation`,
          formData
        )
          .done((verificationInfo: verificationService.VerificationStatus) => {
            if (plootoUtils.IsNullOrUndefined(verificationInfo) || verificationInfo.error) {
              dfd.reject(verificationInfo);
              return;
            }
            analytics.send(
              'action',
              'user-verification',
              'user-verification-completed',
              companyService.Instance.CompanyId()
            );

            dfd.resolve(verificationInfo);
          })
          .fail((verificationInfo: verificationService.VerificationStatus) => {
            dfd.reject(verificationInfo);
          });
      })
      .promise();
  }

  public PostBusinessDirectors(
    companyId: string,
    formData: addModels.BusinessDirectorsAddModel
  ): JQueryPromise<verificationService.VerificationStatus> {
    return system
      .defer<verificationService.VerificationStatus>((dfd) => {
        userService.Instance.AuthorizedPost(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/companies/${companyId}/verification/businessInformation/directors`,
          formData
        )
          .done((verificationInfo: verificationService.VerificationStatus) => {
            if (plootoUtils.IsNullOrUndefined(verificationInfo) || verificationInfo.error) {
              dfd.reject(verificationInfo);
              return;
            }
            dfd.resolve(verificationInfo);
          })
          .fail((verificationInfo: verificationService.VerificationStatus) => {
            dfd.reject(verificationInfo);
          });
      })
      .promise();
  }

  public GetBusinessInformationStatus(
    companyId
  ): JQueryPromise<viewModels.BusinessInformationStatusViewModel> {
    return system
      .defer<viewModels.BusinessInformationStatusViewModel>((dfd) => {
        userService.Instance.AuthorizedGet(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/companies/${companyId}/verification/businessInformation`
        )
          .then((verificationInfo) => {
            if (plootoUtils.IsNullOrUndefined(verificationInfo) || verificationInfo.error) {
              dfd.reject(verificationInfo);
              return;
            }
            analytics.send(
              'action',
              'company-registration',
              'company-registration-completed',
              companyService.Instance.CompanyId()
            );
            dfd.resolve(verificationInfo);
          })
          .fail((verificationInfo: verificationService.VerificationStatus) => {
            dfd.reject(verificationInfo);
          });
      })
      .promise();
  }

  public PostCompanyBusinessDetails(
    companyId: string,
    personalInformation: verificationService.CompanyBusinessDetailsBindingModel
  ): JQueryPromise<any> {
    return system
      .defer((dfd) => {
        userService.Instance.AuthorizedPost(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/companies/${companyId}/verification/businessDetails`,
          personalInformation
        )
          .done((verificationInfo: verificationService.VerificationStatus) => {
            if (plootoUtils.IsNullOrUndefined(verificationInfo) || verificationInfo.error) {
              dfd.reject(verificationInfo);
              return;
            }
            dfd.resolve(verificationInfo);
          })
          .fail((verificationInfo: verificationService.VerificationStatus) => {
            dfd.reject(verificationInfo);
          });
      })
      .promise();
  }

  public PostCompanyAddressDetails(
    companyId: string,
    addressInformation: verificationService.AddressInformationBindingModel
  ): JQueryPromise<any> {
    return system
      .defer((dfd) => {
        userService.Instance.AuthorizedPost(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/companies/${companyId}/verification/addressDetails`,
          addressInformation
        )
          .done((verificationInfo: verificationService.VerificationStatus) => {
            if (plootoUtils.IsNullOrUndefined(verificationInfo) || verificationInfo.error) {
              dfd.reject(verificationInfo);
              return;
            }
            dfd.resolve(verificationInfo);
          })
          .fail((verificationInfo: verificationService.VerificationStatus) => {
            dfd.reject(verificationInfo);
          });
      })
      .promise();
  }

  public SetCompanyVerificationStatusUploadRequired(
    companyId: string,
    userId: string,
    status: string
  ): JQueryPromise<verificationService.VerificationStatus> {
    return system
      .defer<verificationService.VerificationStatus>((dfd) => {
        userService.Instance.AuthorizedPut(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/companies/${companyId}/verification/company/${userId}/${status}`
        )
          .then((data, textStatus, request) => {
            if (plootoUtils.IsNullOrUndefined(data) || data.error) {
              dfd.reject(data);
              return;
            }
            dfd.resolve(data);
          })
          .fail((error) => {
            dfd.reject(error);
          });
      })
      .promise();
  }
}
export const Instance = new VerificationCompanyService();
